import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  approveOrganizationBillingApi,
  getOrganizationApi,
  getOrganizationRosterApi,
  recalculateOrganizationPricingApi,
  updateOrganizationApi,
  getOrganizationPlanTiersApi,
  updateOrganizationPlanTiersApi,
  deleteRosterMemberApi,
} from '../api/organizations.api';
import { apiHandler, unwrapData } from '../utils/apiHandler';
import PageHeader from '../components/PageHeader';
import Pagination from '../components/Pagination';
import DeleteOrganizationModal from '../components/DeleteOrganizationModal';
import { usePagination } from '../hooks/usePagination';
import { formatMoney } from '../features/sponsor-pricing/sponsorPricingFormatters';

const SETUP_STATUS_LABELS = {
  draft: 'Draft',
  pending_billing: 'Pending billing',
  pending_review: 'Pending review',
  active: 'Active',
};

const STAT_ICONS = {
  Users: { icon: 'fa-users', bg: '#EFF6FF', color: '#1D4ED8' },
  Paid: { icon: 'fa-credit-card', bg: '#F0FDF4', color: '#15803D' },
  Freemium: { icon: 'fa-user', bg: '#F1F5F9', color: '#64748B' },
  'TC active': { icon: 'fa-shield-halved', bg: '#FFF7ED', color: '#C2410C' },
};

const TYPE_CLS = {
  Sponsor: 'org-type-employer',
  Referral: 'org-type-partner',
  Employer: 'org-type-employer',
  University: 'org-type-university',
  Church: 'org-type-church',
  Partner: 'org-type-partner',
};

function formatSetupStatus(value) {
  return SETUP_STATUS_LABELS[value] || value || '—';
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function initials(name) {
  return (name || 'OR')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

function toastStyle(type) {
  if (type === 'success') {
    return { background: '#F0FDF4', border: '1px solid #b7e5bb', color: '#15803D' };
  }
  if (type === 'error') {
    return { background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C' };
  }
  return { background: '#EFF6FF', border: '1px solid #BFDBFE', color: '#1D4ED8' };
}

function OrgStatusBadge({ status }) {
  const key = (status || '').toLowerCase();
  const cls = {
    active: 'od-status-active',
    pending: 'od-status-pending',
    inactive: 'od-status-inactive',
    suspended: 'od-status-inactive',
  }[key] || 'od-status-inactive';
  return (
    <span className={`od-status-badge ${cls}`}>
      <span className="od-status-dot" />
      {status || '—'}
    </span>
  );
}

function SetupStatusBadge({ status }) {
  const key = status || 'draft';
  return (
    <span className={`od-setup-badge od-setup-${key}`}>
      {formatSetupStatus(key)}
    </span>
  );
}


function AdminOrgPlanTiersCard({ orgId, showToast }) {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasCustomSelection, setHasCustomSelection] = useState(false);

  const loadTiers = async () => {
    if (!orgId) return;
    setLoading(true);
    const result = await apiHandler(() => getOrganizationPlanTiersApi(orgId));
    if (result.success) {
      const data = unwrapData(result);
      setTiers(data?.tiers || []);
      setHasCustomSelection(Boolean(data?.hasCustomPlanSelection));
    } else {
      if (showToast) showToast(result.message || 'Failed to load allowed plan tiers', 'error');
    }
    setLoading(false);
  };

  useEffect(() => {
    loadTiers();
  }, [orgId]);

  const handleToggleTier = (tierId) => {
    setTiers((prev) =>
      prev.map((t) => (t.id === tierId ? { ...t, isSelected: !t.isSelected } : t))
    );
  };

  const handleSave = async () => {
    const selectedTierIds = tiers.filter((t) => t.isSelected).map((t) => t.id);
    setSaving(true);
    const result = await apiHandler(() =>
      updateOrganizationPlanTiersApi(orgId, { selectedTierIds })
    );
    if (result.success) {
      const data = unwrapData(result);
      setTiers(data?.tiers || []);
      setHasCustomSelection(Boolean(data?.hasCustomPlanSelection));
      if (showToast) {
        showToast(
          selectedTierIds.length > 0
            ? 'Allowed plan tiers saved successfully'
            : 'Plan restrictions cleared (All plans allowed)',
          'success'
        );
      }
    } else {
      if (showToast) showToast(result.message || 'Failed to save allowed plan tiers', 'error');
    }
    setSaving(false);
  };

  const handleClearRestrictions = () => {
    setTiers((prev) => prev.map((t) => ({ ...t, isSelected: false })));
  };

  const selectedCount = tiers.filter((t) => t.isSelected).length;

  return (
    <div className="od-card" style={{ marginBottom: 20 }}>
      <div className="od-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="od-card-title">
          <span className="od-card-icon od-icon-orange">
            <i className="fas fa-layer-group" />
          </span>
          Allowed Payment Plans (Deep Link Filter)
        </div>
        <div>
          {hasCustomSelection ? (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: '#FEF3C7',
              color: '#92400E',
              padding: '4px 10px',
              borderRadius: 12,
              fontSize: 12,
              fontWeight: 600
            }}>
              <i className="fas fa-filter" style={{ fontSize: 10 }} />
              {selectedCount} Tier{selectedCount > 1 ? 's' : ''} Restricted
            </span>
          ) : (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: '#F1F5F9',
              color: '#475569',
              padding: '4px 10px',
              borderRadius: 12,
              fontSize: 12,
              fontWeight: 500
            }}>
              <i className="fas fa-globe" style={{ fontSize: 10 }} />
              All Plans Visible (Default)
            </span>
          )}
        </div>
      </div>
      <div className="od-card-body">
        <p style={{ fontSize: 13, color: '#64748B', marginTop: 0, marginBottom: 16, lineHeight: 1.5 }}>
          Select which subscription plans are visible when a user joins or registers via this organization's deep link / QR code.
          If <strong>no plans are checked</strong>, users will see all default plans (Free, Single, and Group).
        </p>

        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '16px 0', color: '#64748B', fontSize: 13 }}>
            <i className="fas fa-spinner fa-spin" /> Loading available plan tiers...
          </div>
        ) : tiers.length === 0 ? (
          <p style={{ fontSize: 13, color: '#94A3B8' }}>No active plan tiers found in catalog.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
            {tiers.map((t) => {
              const isChecked = Boolean(t.isSelected);
              return (
                <label
                  key={t.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 14px',
                    borderRadius: 8,
                    border: `1.5px solid ${isChecked ? '#2563EB' : '#E2E8F0'}`,
                    background: isChecked ? '#EFF6FF' : '#FFFFFF',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => handleToggleTier(t.id)}
                    style={{ width: 17, height: 17, accentColor: '#2563EB', cursor: 'pointer' }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 600, fontSize: 14, color: '#1E293B' }}>
                        {t.name || t.tier}
                      </span>
                      {t.isPopular && (
                        <span style={{ fontSize: 11, background: '#EFF6FF', color: '#1D4ED8', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>
                          Popular
                        </span>
                      )}
                      <span style={{ fontSize: 11, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        [{t.tier}]
                      </span>
                    </div>
                    {t.subtitle && (
                      <div style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>
                        {t.subtitle}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingTop: 12, borderTop: '1px solid #E2E8F0' }}>
          <div>
            {selectedCount > 0 && (
              <button
                type="button"
                className="od-header-btn od-header-btn-ghost"
                onClick={handleClearRestrictions}
                disabled={saving || loading}
                style={{ fontSize: 12 }}
              >
                Reset to All Plans
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="action-btn btn-blue"
              onClick={handleSave}
              disabled={saving || loading}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              {saving ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-check" />}
              {saving ? 'Saving...' : 'Save Allowed Plans'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminGroupCodeSection({ enterprise, org, showToast }) {
  const [copiedField, setCopiedField] = useState(null);

  const groupCode = enterprise?.referralCode || enterprise?.groupCode || org?.referralCode || org?.code || '';
  const inviteUrl = enterprise?.qrSmartLink || (groupCode ? `https://portal.myguardianlink.com/app?group=${groupCode}` : enterprise?.inviteUrl || '');
  const qrSmartLink = inviteUrl;

  const handleCopy = async (text, fieldName, successMsg) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      if (showToast) {
        showToast(successMsg || `${fieldName} copied to clipboard`, 'success');
      }
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      if (showToast) showToast('Failed to copy to clipboard', 'error');
    }
  };

  const fields = [
    { label: 'Invite Link', value: inviteUrl, key: 'invite', placeholder: 'https://portal.myguardianlink.com/app?group=...' },
    { label: 'Group Code', value: groupCode, key: 'code', isBold: true, placeholder: 'GROUP_CODE' },
  ];

  return (
    <div className="od-card">
      <div className="od-card-header">
        <div className="od-card-title">
          <span className="od-card-icon od-icon-blue"><i className="fas fa-qrcode" /></span>
          Onboarding &amp; invite
        </div>
      </div>
      <div className="od-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {(enterprise?.onboardingContact?.name || enterprise?.onboardingContact?.email) && (
          <div style={{ paddingBottom: 10, borderBottom: '1px solid #F1F5F9' }}>
            <InfoList
              rows={[
                { key: 'Contact', value: enterprise.onboardingContact?.name },
                { key: 'Email', value: enterprise.onboardingContact?.email },
                { key: 'Phone', value: enterprise.onboardingContact?.phone },
              ]}
            />
          </div>
        )}

        <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Left: Dynamic QR Code */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <div
              onClick={() => handleCopy(qrSmartLink, 'qr', 'App Smart Link copied! Scan QR code with a mobile camera to test app download.')}
              style={{
                cursor: 'pointer',
                borderRadius: 10,
                border: '1px solid #E2E8F0',
                padding: 8,
                background: '#FFFFFF',
                boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                transition: 'opacity 0.15s',
              }}
              title="Click to copy QR Smart Link"
            >
              {qrSmartLink ? (
                <QRCodeSVG value={qrSmartLink} size={84} level="M" />
              ) : enterprise?.qrCodeDataUrl ? (
                <img src={enterprise.qrCodeDataUrl} alt="Group QR Code" style={{ width: 84, height: 84 }} />
              ) : (
                <div style={{ width: 84, height: 84, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94A3B8', fontSize: 11 }}>
                  No Code
                </div>
              )}
            </div>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#64748B' }}>
              {copiedField === 'qr' ? (
                <span style={{ color: '#16A34A' }}><i className="fas fa-check" /> Copied!</span>
              ) : (
                'Scan or Tap'
              )}
            </span>
          </div>

          {/* Right: Copyable Link Inputs */}
          <div style={{ flex: 1, minWidth: 220, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {fields.map((field) => (
              <div key={field.key} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#475569' }}>
                  {field.label}
                </label>
                <div
                  style={{
                    display: 'flex',
                    height: 36,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    overflow: 'hidden',
                    borderRadius: 8,
                    border: '1px solid #CBD5E1',
                    background: '#F8FAFC',
                  }}
                >
                  <input
                    type="text"
                    readOnly
                    value={field.value || ''}
                    placeholder={field.placeholder}
                    style={{
                      height: '100%',
                      flex: 1,
                      background: 'transparent',
                      padding: '0 10px',
                      fontSize: 12,
                      border: 'none',
                      outline: 'none',
                      color: field.isBold ? '#0F172A' : '#334155',
                      fontWeight: field.isBold ? 700 : 400,
                      fontFamily: field.key === 'code' ? "'DM Mono', monospace" : 'inherit',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => handleCopy(field.value, field.key, `${field.label} copied to clipboard`)}
                    style={{
                      display: 'flex',
                      height: '100%',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#074f78',
                      color: '#FFFFFF',
                      padding: '0 12px',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 12,
                      transition: 'background 0.15s',
                    }}
                    title={`Copy ${field.label}`}
                  >
                    {copiedField === field.key ? (
                      <i className="fas fa-check" style={{ color: '#4ADE80' }} />
                    ) : (
                      <i className="fas fa-copy" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoList({ rows }) {
  return (
    <div className="od-info-list">
      {rows.map(({ key, value, icon }) => (
        <div key={key} className="od-info-row">
          <span className="od-info-key">
            {icon && <i className={`fas ${icon}`} />}
            {key}
          </span>
          <span className="od-info-val">{value ?? '—'}</span>
        </div>
      ))}
    </div>
  );
}

export default function OrganizationDetail() {
  const navigate = useNavigate();
  const { orgId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState(
    ['sponsor', 'roster', 'attribution', 'members'].includes(tabParam) ? tabParam : 'overview'
  );
  const [roster, setRoster] = useState([]);
  const [approving, setApproving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [editOfferOpen, setEditOfferOpen] = useState(false);
  const [savingOffer, setSavingOffer] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteRosterTarget, setDeleteRosterTarget] = useState(null);
  const [deleteRosterLoading, setDeleteRosterLoading] = useState(false);
  const {
    page,
    limit,
    loading: rosterLoading,
    setLoading: setRosterLoading,
    applyPaginationMeta,
    paginationProps,
  } = usePagination(20);

  function showToast(msg, type = 'info') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  }

  function handleTabChange(tab) {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    if (tab === 'overview') {
      next.delete('tab');
    } else {
      next.set('tab', tab);
    }
    setSearchParams(next, { replace: true });
  }

  async function loadOrg() {
    setLoading(true);
    const result = await apiHandler(() => getOrganizationApi(orgId));
    if (result.success) {
      setData(unwrapData(result));
    } else {
      showToast(result.message || 'Failed to load organization', 'error');
    }
    setLoading(false);
  }

  
  const handleConfirmDeleteRoster = async () => {
    if (!deleteRosterTarget || deleteRosterLoading) return;
    setDeleteRosterLoading(true);
    const result = await apiHandler(() => deleteRosterMemberApi(orgId, deleteRosterTarget.id));
    if (result.success) {
      showToast(result.message || 'Roster member removed successfully', 'success');
      setDeleteRosterTarget(null);
      await loadRoster(page);
      await loadOrg();
    } else {
      showToast(result.message || 'Failed to remove roster member', 'error');
    }
    setDeleteRosterLoading(false);
  };

  async function handleSaveOffer(offerData) {
    setSavingOffer(true);
    const result = await apiHandler(() => updateOrganizationApi(orgId, offerData));
    if (result.success) {
      showToast('Referral offer settings updated successfully', 'success');
      setEditOfferOpen(false);
      await loadOrg();
    } else {
      showToast(result.message || 'Failed to update offer settings', 'error');
    }
    setSavingOffer(false);
  }

  const [deletingRosterId, setDeletingRosterId] = useState(null);

  async function handleDeleteRosterMember(row) {
    const memberName = `${row.firstName || ''} ${row.lastName || ''}`.trim() || row.email || row.mobile || 'this member';
    const isSponsor = (enterprise?.orgType || org?.type || '').toLowerCase() !== 'referral';
    const confirmationNotice = isSponsor
      ? `Are you sure you want to remove ${memberName} from this organization's roster?\n\n[SPONSOR ORG]: The member's sponsored subscription and benefits will be REVOKED IMMEDIATELY.`
      : `Are you sure you want to remove ${memberName} from this organization's roster?\n\n[REFERRAL ORG]: The member's current paid period remains active, but they will NOT receive the referral discount on future renewals or purchases.`;

    if (!window.confirm(confirmationNotice)) return;

    setDeletingRosterId(row.id);
    const result = await apiHandler(() => deleteRosterMemberApi(orgId, row.id));
    if (result.success) {
      showToast(`Roster member ${memberName} removed successfully`, 'success');
      await loadRoster(page);
      await loadOrg();
    } else {
      showToast(result.message || 'Failed to remove roster member', 'error');
    }
    setDeletingRosterId(null);
  }

  async function loadRoster(pageNum = page) {
    setRosterLoading(true);
    const result = await apiHandler(() => getOrganizationRosterApi(orgId, { page: pageNum, limit }));
    if (result.success) {
      const payload = unwrapData(result);
      setRoster(payload?.roster || []);
      applyPaginationMeta(payload);
    } else {
      showToast(result.message || 'Failed to load roster', 'error');
    }
    setRosterLoading(false);
  }

  useEffect(() => {
    if (orgId) loadOrg();
  }, [orgId]);

  useEffect(() => {
    if (!data?.enterpriseSetup && ['sponsor', 'roster', 'attribution'].includes(activeTab)) {
      setActiveTab('overview');
    }
  }, [data, activeTab]);

  useEffect(() => {
    if (orgId && activeTab === 'roster') {
      loadRoster(page);
    }
  }, [orgId, activeTab, page]);

  const org = data?.organization || {};
  const metrics = data?.metrics || {};
  const campaigns = data?.campaigns || [];
  const enterprise = data?.enterpriseSetup;
  const members = data?.raw?.userOrganizations || [];
  const fromUserId = searchParams.get('fromUser');
  const fromUserMember = members.find((m) => m.userId === fromUserId)?.user;
  const fromUserName = fromUserMember ? `${fromUserMember.firstName || ''} ${fromUserMember.lastName || ''}`.trim() : null;
  const orgName = org.name || org.organization || 'Organization';
  const setupTabLabel = (enterprise?.orgType || org.type || '').toLowerCase() === 'referral'
    ? 'Referral Setup'
    : 'Sponsor Setup';
  const isReferralOrg = (enterprise?.orgType || org.type || '').toLowerCase() === 'referral';

  const stats = [
    { label: 'Users', num: metrics.users },
    { label: 'Paid', num: metrics.paid },
    { label: 'Freemium', num: metrics.freemium },
    { label: 'TC active', num: metrics.tcActive },
  ];

  const handleApproveBilling = async () => {
    if (!window.confirm('Approve billing and activate this sponsor organization?')) return;
    setApproving(true);
    const result = await apiHandler(() => approveOrganizationBillingApi(orgId));
    if (result.success) {
      showToast('Billing approved and organization activated', 'success');
      loadOrg();
    } else {
      showToast(result.message || 'Failed to approve billing', 'error');
    }
    setApproving(false);
  };

  const handleRecalculatePricing = async () => {
    setRecalculating(true);
    const result = await apiHandler(() => recalculateOrganizationPricingApi(orgId));
    if (result.success) {
      showToast('Pricing recalculated', 'success');
      loadOrg();
    } else {
      showToast(result.message || 'Failed to recalculate pricing', 'error');
    }
    setRecalculating(false);
  };

  const copyInviteUrl = async () => {
    if (!enterprise?.inviteUrl) return;
    try {
      await navigator.clipboard.writeText(enterprise.inviteUrl);
      showToast('Invite URL copied', 'success');
    } catch {
      showToast('Could not copy invite URL', 'error');
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'fa-chart-pie' },
    { id: 'members', label: 'Members', icon: 'fa-users' },
    ...(enterprise ? [
      { id: 'sponsor', label: setupTabLabel, icon: 'fa-building-columns' },
      ...(isReferralOrg ? [{ id: 'attribution', label: 'Attribution', icon: 'fa-hand-holding-dollar' }] : []),
      { id: 'roster', label: 'Roster', icon: 'fa-list-check' },
    ] : []),
  ];

  return (
    <>
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: 70,
            right: 20,
            zIndex: 9999,
            padding: '10px 18px',
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,.08)',
            ...toastStyle(toast.type),
          }}
        >
          {toast.msg}
        </div>
      )}

      <PageHeader
        title="Organization Detail"
        icon="fa-building"
        trail={[
          { label: 'Organizations', to: '/organizations' },
          { label: orgName },
        ]}
        actions={(
          <div style={{ display: 'flex', gap: 10 }}>
            {fromUserId && (
              <button
                type="button"
                className="od-header-btn od-header-btn-ghost"
                onClick={() => navigate(`/users/${fromUserId}`)}
                style={{ border: '1px solid #CBD5E1', background: '#F8FAFC', color: '#334155' }}
              >
                <i className="fas fa-user" style={{ marginRight: 4 }} />
                Back to {fromUserName || 'User'}
              </button>
            )}
            <button
              type="button"
              className="od-header-btn od-header-btn-ghost"
              onClick={() => navigate('/organizations')}
            >
              <i className="fas fa-arrow-left" />
              Back to Organizations
            </button>
            <button
              type="button"
              className="od-header-btn"
              onClick={() => setDeleteModalOpen(true)}
              style={{ border: '1px solid #FECACA', background: '#FEF2F2', color: '#DC2626' }}
            >
              <i className="fas fa-trash-can" style={{ marginRight: 4 }} />
              Delete Organization
            </button>
          </div>
        )}
      />

      {loading ? (
        <div className="od-empty-state">
          <i className="fas fa-spinner fa-spin" />
          Loading organization…
        </div>
      ) : !data ? (
        <div className="od-empty-state">
          <i className="fas fa-building-circle-xmark" />
          Organization not found.
        </div>
      ) : (
        <div className="fade-up">
          <div className="al-stat-row" style={{ marginBottom: 20 }}>
            {stats.map((s) => {
              const meta = STAT_ICONS[s.label] || STAT_ICONS.Users;
              return (
                <div key={s.label} className="al-stat-card">
                  <div className="al-stat-icon" style={{ background: meta.bg, color: meta.color }}>
                    <i className={`fas ${meta.icon}`} />
                  </div>
                  <div>
                    <div className="al-stat-num">{s.num ?? '—'}</div>
                    <div className="al-stat-label">{s.label}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {enterprise?.canApproveBilling && (
            <div className="od-review-banner">
              <div className="od-review-banner-text">
                <strong>
                  {enterprise.setupStatus === 'active' && (org.status || '').toLowerCase() === 'pending'
                    ? 'Organization needs activation'
                    : 'Billing pending review'}
                </strong>
                {enterprise.setupStatus === 'active' && (org.status || '').toLowerCase() === 'pending'
                  ? 'Payment is recorded but organization status is still pending — approve to fully activate and enable associate invites.'
                  : 'Payment received — approve to activate this organization and enable associate invites.'}
              </div>
              <button
                type="button"
                className="action-btn btn-green"
                onClick={handleApproveBilling}
                disabled={approving}
              >
                <i className="fas fa-check" />
                {approving ? 'Approving…' : 'Approve billing'}
              </button>
            </div>
          )}

          <div className="od-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`od-tab ${activeTab === tab.id ? 'od-tab-active' : ''}`}
                onClick={() => handleTabChange(tab.id)}
              >
                <i className={`fas ${tab.icon}`} />
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'overview' && (
            <div className="od-grid">
              <div className="od-card">
                <div className="od-org-hero">
                  <div className="od-org-avatar">{initials(orgName)}</div>
                  <div>
                    <div className="od-org-name">{orgName}</div>
                    <div className="od-org-sub">
                      {org.type || 'Organization'}
                      {org.state ? ` · ${org.state}` : ''}
                    </div>
                  </div>
                </div>
                <div className="od-card-body">
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                    <span className={`org-type ${TYPE_CLS[org.type] || 'org-type-default'}`}>
                      {org.type || '—'}
                    </span>
                    <OrgStatusBadge status={org.status} />
                    {enterprise && (
                      <SetupStatusBadge status={enterprise.setupStatus} />
                    )}
                  </div>
                  <InfoList
                    rows={[
                      { key: 'Contact name', value: org.contactName, icon: 'fa-user' },
                      { key: 'Contact email', value: org.contactEmail, icon: 'fa-envelope' },
                      { key: 'Contact phone', value: org.contactPhone, icon: 'fa-phone' },
                      { key: 'Notes', value: org.notes || 'No notes available.', icon: 'fa-note-sticky' },
                    ]}
                  />
                </div>
              </div>

              {enterprise ? (
                <div className="od-card">
                  <div className="od-card-header">
                    <div className="od-card-title">
                      <span className="od-card-icon od-icon-orange">
                        <i className="fas fa-tags" />
                      </span>
                      Enterprise summary
                    </div>
                    <SetupStatusBadge status={enterprise.setupStatus} />
                  </div>
                  <div className="od-card-body">
                    <InfoList
                      rows={[
                        { key: 'Expected associates', value: enterprise.expectedAssociates, icon: 'fa-users' },
                        { key: 'Pricing tier', value: enterprise.matchedTierNumber != null ? `Tier #${enterprise.matchedTierNumber}` : '—', icon: 'fa-layer-group' },
                        { key: 'Annual total', value: formatMoney(enterprise.expectedAnnualTotal), icon: 'fa-dollar-sign' },
                        { key: 'Billing method', value: enterprise.billingMethod?.toUpperCase() || '—', icon: 'fa-credit-card' },
                        { key: 'Paid on', value: formatDate(enterprise.enterprisePaidAt), icon: 'fa-calendar-check' },
                        { key: 'Valid until', value: formatDate(enterprise.validUntil), icon: 'fa-calendar' },
                        { key: 'Approved on', value: formatDate(enterprise.enterpriseBillingApprovedAt), icon: 'fa-calendar' },
                        { key: 'CSV uploaded', value: enterprise.csvUploaded ? 'Yes' : 'No', icon: 'fa-file-csv' },
                        { key: 'Group code', value: enterprise.referralCode, icon: 'fa-qrcode' },
                      ]}
                    />
                    <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="action-btn btn-blue"
                        onClick={() => handleTabChange('sponsor')}
                      >
                        View setup
                      </button>
                      <button
                        type="button"
                        className="od-header-btn od-header-btn-ghost"
                        onClick={() => handleTabChange('roster')}
                      >
                        View roster
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="od-card">
                  <div className="od-card-header">
                    <div className="od-card-title">
                      <span className="od-card-icon od-icon-navy">
                        <i className="fas fa-circle-info" />
                      </span>
                      Organization type
                    </div>
                  </div>
                  <div className="od-card-body">
                    <p style={{ fontSize: 13, color: '#64748B', margin: 0, lineHeight: 1.6 }}>
                      This is a legacy organization. Enterprise sponsor setup tabs are available
                      only for <strong>Sponsor</strong> and <strong>Referral</strong> org types.
                    </p>
                  </div>
                </div>
              )}

              <div className="od-card">
                <div className="od-card-header">
                  <div className="od-card-title">
                    <span className="od-card-icon od-icon-blue">
                      <i className="fas fa-bullhorn" />
                    </span>
                    Recent campaigns
                  </div>
                </div>
                <div className="od-card-body" style={{ padding: campaigns.length ? '0 0 12px' : undefined }}>
                  {campaigns.length === 0 ? (
                    <div className="od-empty-state" style={{ padding: '24px 16px' }}>
                      <i className="fas fa-inbox" />
                      No recent campaigns for this organization.
                    </div>
                  ) : (
                    <table className="od-campaign-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Status</th>
                          <th>Open rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {campaigns.map((c) => (
                          <tr key={c.id}>
                            <td className="od-campaign-name">{c.campaign}</td>
                            <td>{c.status}</td>
                            <td className={c.open && c.open !== '—' ? 'od-campaign-open' : 'od-campaign-dash'}>
                              {c.open || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'sponsor' && enterprise && (
            <>
              <div className="od-card" style={{ marginBottom: 20 }}>
                <div className="od-card-header">
                  <div className="od-card-title">
                    <span className="od-card-icon od-icon-orange">
                      <i className="fas fa-clipboard-check" />
                    </span>
                    Setup &amp; billing actions
                  </div>
                  <SetupStatusBadge status={enterprise.setupStatus} />
                </div>
                <div className="od-card-body">
                  <div className="od-action-group">
                    <div className="od-action-row-2">
                      {!isReferralOrg && enterprise.canApproveBilling && (
                        <button type="button" className="action-btn btn-green od-action-full" onClick={handleApproveBilling} disabled={approving}>
                          <i className="fas fa-check" />
                          {approving ? 'Approving…' : 'Approve billing'}
                        </button>
                      )}
                      {!isReferralOrg && (
                        <button type="button" className="action-btn btn-blue od-action-full" onClick={handleRecalculatePricing} disabled={recalculating}>
                          <i className="fas fa-rotate" />
                          {recalculating ? 'Recalculating…' : 'Recalculate pricing'}
                        </button>
                      )}
                    </div>
                    <Link to="/sponsor-pricing" className="action-btn od-action-full" style={{ justifyContent: 'center', background: '#F8FAFC', border: '1px solid #E2E8F0', color: '#475569' }}>
                      <i className="fas fa-table" />
                      View pricing table
                    </Link>
                  </div>
                </div>
              </div>

              <div className="od-grid">
                {isReferralOrg ? (
                  <div className="od-card">
                    <div className="od-card-header" style={{ display: 'flex', alignItems: 'center', justifyBetween: 'space-between', width: '100%' }}>
                      <div className="od-card-title">
                        <span className="od-card-icon od-icon-orange"><i className="fas fa-percent" /></span>
                        Referral member offer
                      </div>
                      <button
                        type="button"
                        className="action-btn btn-blue"
                        style={{ padding: '6px 12px', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}
                        onClick={() => setEditOfferOpen(true)}
                      >
                        <i className="fas fa-pen-to-square" />
                        Edit
                      </button>
                    </div>
                    <div className="od-card-body">
                      <InfoList
                        rows={[
                          { key: 'Offer type', value: enterprise.referralPreview?.offerType || 'percentage_discount' },
                          { key: 'Discount', value: enterprise.referralPreview?.discountOfferedToMembers != null ? `${enterprise.referralPreview.discountOfferedToMembers}%` : '—' },
                          { key: 'Member annual price', value: enterprise.referralPreview?.priceDisplay || '—' },
                          { key: 'Price without discount', value: enterprise.referralPreview?.basePriceDisplay || '—' },
                          { key: 'Revenue share', value: enterprise.referralPreview?.groupShareLabel || '—' },
                          { key: 'Payout frequency', value: enterprise.referralPreview?.payoutFrequencyLabel || '—' },
                          { key: 'Payout destination', value: enterprise.referralPreview?.payoutDestination || '—' },
                          { key: 'Payment responsibility', value: enterprise.paymentResponsibility === 'user' ? 'Member pays' : enterprise.paymentResponsibility },
                        ]}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="od-card">
                    <div className="od-card-header">
                      <div className="od-card-title">
                        <span className="od-card-icon od-icon-orange"><i className="fas fa-building" /></span>
                        Sponsor group
                      </div>
                    </div>
                    <div className="od-card-body">
                      <InfoList
                        rows={[
                          { key: 'Advisor', value: enterprise.sponsorGroupAdvisor },
                          { key: 'Tier', value: enterprise.matchedTierNumber != null ? `Tier #${enterprise.matchedTierNumber}` : '—' },
                          { key: 'Price / associate', value: formatMoney(enterprise.pricePerAssociateAnnual) },
                          { key: 'Expected associates', value: enterprise.expectedAssociates },
                          { key: 'Annual total', value: formatMoney(enterprise.expectedAnnualTotal) },
                          { key: 'Billing responsibility', value: enterprise.billingResponsibility },
                          { key: 'Onboarding', value: enterprise.onboardingManagement },
                        ]}
                      />
                    </div>
                  </div>
                )}

                <div className="od-card">
                  <div className="od-card-header">
                    <div className="od-card-title">
                      <span className="od-card-icon od-icon-navy"><i className="fas fa-file-invoice-dollar" /></span>
                      Billing contact
                    </div>
                  </div>
                  <div className="od-card-body">
                    <InfoList
                      rows={[
                        { key: 'Name', value: enterprise.billingContact?.name },
                        { key: 'Title', value: enterprise.billingContact?.title },
                        { key: 'Email', value: enterprise.billingContact?.email },
                        { key: 'Phone', value: enterprise.billingContact?.phone },
                        { key: 'Address', value: enterprise.billingContact?.address },
                      ]}
                    />
                  </div>
                </div>

                <AdminGroupCodeSection enterprise={enterprise} org={org} showToast={showToast} />

                <AdminOrgPlanTiersCard orgId={orgId} showToast={showToast} />

                <div className="od-card">
                  <div className="od-card-header">
                    <div className="od-card-title">
                      <span className="od-card-icon od-icon-orange"><i className="fas fa-list" /></span>
                      Roster summary
                    </div>
                  </div>
                  <div className="od-card-body">
                    <InfoList
                      rows={[
                        { key: 'Total rows', value: enterprise.rosterSummary?.total },
                        { key: 'Active / pending', value: enterprise.rosterSummary?.active },
                        { key: 'Forfeited', value: enterprise.rosterSummary?.forfeited },
                      ]}
                    />
                    <button
                      type="button"
                      className="od-header-btn od-header-btn-ghost"
                      style={{ marginTop: 14, width: '100%', justifyContent: 'center' }}
                      onClick={() => handleTabChange('roster')}
                    >
                      Open full roster
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'attribution' && enterprise && isReferralOrg && (
            <div className="od-card">
              <div className="od-card-header">
                <div className="od-card-title">
                  <span className="od-card-icon od-icon-orange"><i className="fas fa-hand-holding-dollar" /></span>
                  Referral attribution
                </div>
              </div>
              <div className="od-card-body">
                <InfoList
                  rows={[
                    { key: 'Revenue share', value: enterprise.referralPreview?.groupShareLabel || '—' },
                    { key: 'Payout frequency', value: enterprise.referralPreview?.payoutFrequencyLabel || '—' },
                    { key: 'Payout destination', value: enterprise.referralPreview?.payoutDestination || '—' },
                    { key: 'Member discount', value: enterprise.referralPreview?.discountOfferedToMembers != null ? `${enterprise.referralPreview.discountOfferedToMembers}%` : '—' },
                  ]}
                />
                <p style={{ fontSize: 13, color: '#64748B', marginTop: 12 }}>
                  Detailed attribution ledger is recorded when referral members complete payment.
                  Use the owner API <code>GET /enterprise/setup/:orgId/referral-stats</code> for live counts.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'members' && (
            <div className="org-table-card">
              <div className="org-table-header">
                <div>
                  <div className="org-table-title">
                    <i className="fas fa-users" />
                    Joined Members
                  </div>
                  <div className="org-table-meta">
                    {members.length} member{members.length !== 1 ? 's' : ''} in this organization
                  </div>
                </div>
              </div>
              <div className="org-table-wrap">
                <table className="org-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Mobile</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Joined Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.length === 0 ? (
                      <tr>
                        <td colSpan={6} style={{ textAlign: 'center', padding: '24px 0', color: '#64748B' }}>
                          No joined members found.
                        </td>
                      </tr>
                    ) : (
                      members.map((memberOrg) => {
                        const u = memberOrg.user || {};
                        const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || '—';
                        return (
                          <tr key={memberOrg.id}>
                            <td>
                              <Link to={`/users/${u.id}`} className="org-name-text" style={{ textDecoration: 'none', color: '#1E293B', fontWeight: 600 }}>
                                {name}
                              </Link>
                              {memberOrg.role === 'owner' && (
                                <span style={{ marginLeft: 8, fontSize: 10, background: '#EFF6FF', color: '#1D4ED8', padding: '2px 6px', borderRadius: 4, fontWeight: 500 }}>
                                  Owner
                                </span>
                              )}
                            </td>
                            <td>{u.email || '—'}</td>
                            <td>{u.mobile || '—'}</td>
                            <td>
                              <span style={{ fontSize: 12, textTransform: 'capitalize' }}>
                                {memberOrg.role || 'member'}
                              </span>
                            </td>
                            <td>
                              <span className={`org-status org-status-${u.status === 'active' ? 'active' : 'pending'}`}>
                                {u.status || 'pending'}
                              </span>
                            </td>
                            <td>{formatDate(memberOrg.joinedAt)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'roster' && enterprise && (
            <div className="org-table-card">
              <div className="org-table-header">
                <div>
                  <div className="org-table-title">
                    <i className="fas fa-list-check" />
                    Associate roster
                  </div>
                  <div className="org-table-meta">
                    {roster.length} row{roster.length !== 1 ? 's' : ''} on this page
                  </div>
                </div>
              </div>
              <div className="org-table-wrap">
                <table className="org-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Mobile</th>
                      <th>Status</th>
                      <th>Activation deadline</th>
                      <th style={{ textAlign: 'center', width: 90 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rosterLoading ? (
                      <tr><td colSpan={6}>Loading roster…</td></tr>
                    ) : roster.length === 0 ? (
                      <tr><td colSpan={6}>No roster rows found.</td></tr>
                    ) : roster.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <span className="org-name-text">
                            {`${row.firstName || ''} ${row.lastName || ''}`.trim() || '—'}
                          </span>
                        </td>
                        <td>{row.email || '—'}</td>
                        <td>{row.mobile || '—'}</td>
                                                <td>
                          <span className={`org-status org-status-${row.status === 'active' ? 'active' : 'pending'}`}>
                            {row.status}
                          </span>
                        </td>
                        <td>{row.activationDeadline ? new Date(row.activationDeadline).toLocaleDateString() : '—'}</td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            type="button"
                            onClick={() => setDeleteRosterTarget(row)}
                            title="Remove Roster Member"
                            style={{
                              background: '#FEF2F2',
                              border: '1px solid #FECACA',
                              color: '#DC2626',
                              width: 32,
                              height: 32,
                              borderRadius: 6,
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.15s ease'
                            }}
                          >
                            <i className="fas fa-trash-can" style={{ fontSize: 13 }} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="org-footer">
                <Pagination {...paginationProps} />
              </div>
            </div>
          )}
        </div>
      )}

      <EditReferralOfferModal
        org={org}
        enterprise={enterprise}
        isOpen={editOfferOpen}
        onClose={() => setEditOfferOpen(false)}
        onSave={handleSaveOffer}
        saving={savingOffer}
      />

            {deleteRosterTarget && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(4px)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={() => !deleteRosterLoading && setDeleteRosterTarget(null)}
        >
          <div
            style={{
              background: '#FFFFFF',
              borderRadius: 16,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              width: '100%',
              maxWidth: 480,
              padding: '24px 28px',
              animation: 'fadeUp 0.2s ease-out',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: '#FEF2F2',
                  color: '#DC2626',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                }}
              >
                <i className="fas fa-user-minus" />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0F172A' }}>
                  Remove Roster Member
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: 13, color: '#64748B' }}>
                  {orgName}
                </p>
              </div>
            </div>

            <p style={{ fontSize: 14, color: '#334155', lineHeight: 1.5, margin: '0 0 16px' }}>
              Are you sure you want to remove <strong>{`${deleteRosterTarget.firstName || ''} ${deleteRosterTarget.lastName || ''}`.trim() || deleteRosterTarget.email || deleteRosterTarget.mobile || 'this member'}</strong> from the organization's roster?
            </p>

            {isReferralOrg ? (
              <div style={{
                background: '#EFF6FF',
                border: '1px solid #BFDBFE',
                borderRadius: 8,
                padding: '12px 14px',
                marginBottom: 20,
                fontSize: 13,
                color: '#1E40AF',
                lineHeight: 1.5
              }}>
                <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <i className="fas fa-info-circle" /> Referral Member Policy
                </div>
                The user has self-billed. Current access remains active until the end of their paid period. The referral discount will be removed for future plan purchases and renewals.
              </div>
            ) : (
              <div style={{
                background: '#FFF7ED',
                border: '1px solid #FFEDD5',
                borderRadius: 8,
                padding: '12px 14px',
                marginBottom: 20,
                fontSize: 13,
                color: '#C2410C',
                lineHeight: 1.5
              }}>
                <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <i className="fas fa-triangle-exclamation" /> Sponsor Member Policy
                </div>
                All sponsored benefits and emergency alert access for this user will be revoked immediately.
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                type="button"
                className="action-btn"
                onClick={() => setDeleteRosterTarget(null)}
                disabled={deleteRosterLoading}
                style={{ background: '#F1F5F9', border: '1px solid #CBD5E1', color: '#475569', height: 38, padding: '0 16px', borderRadius: 8 }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="action-btn"
                onClick={handleConfirmDeleteRoster}
                disabled={deleteRosterLoading}
                style={{
                  background: '#DC2626',
                  color: '#FFFFFF',
                  height: 38,
                  padding: '0 20px',
                  borderRadius: 8,
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <i className={deleteRosterLoading ? 'fas fa-spinner fa-spin' : 'fas fa-trash-can'} />
                {deleteRosterLoading ? 'Removing...' : 'Remove Member'}
              </button>
            </div>
          </div>
        </div>
      )}

      <DeleteOrganizationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        orgId={orgId}
        orgName={orgName}
        hasRoster={(enterprise?.rosterSummary?.total ?? roster?.length ?? metrics?.users ?? 0) > 0}
        onRosterCleared={async () => {
          await loadOrg();
          if (activeTab === 'roster') await loadRoster(1);
        }}
        onDeleted={() => {
          setDeleteModalOpen(false);
          navigate('/organizations');
        }}
      />
    </>
  );
}

function EditReferralOfferModal({ org, enterprise, isOpen, onClose, onSave, saving }) {
  const [memberDiscountPercent, setMemberDiscountPercent] = useState(
    enterprise?.referralPreview?.discountOfferedToMembers ?? org?.memberDiscountPercent ?? 0
  );
  const [rosterListedRatePct, setRosterListedRatePct] = useState(
    enterprise?.referralPreview?.commissionModel?.rosterListedRatePct ?? org?.rosterListedRatePct ?? 15
  );
  const [generalReferralRatePct, setGeneralReferralRatePct] = useState(
    enterprise?.referralPreview?.commissionModel?.generalReferralRatePct ?? org?.generalReferralRatePct ?? 10
  );
  const [payoutFrequency, setPayoutFrequency] = useState(
    org?.payoutFrequency || enterprise?.referralPreview?.payoutFrequency || 'monthly'
  );

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setMemberDiscountPercent(
        enterprise?.referralPreview?.discountOfferedToMembers ?? org?.memberDiscountPercent ?? 0
      );
      setRosterListedRatePct(
        enterprise?.referralPreview?.commissionModel?.rosterListedRatePct ?? org?.rosterListedRatePct ?? 15
      );
      setGeneralReferralRatePct(
        enterprise?.referralPreview?.commissionModel?.generalReferralRatePct ?? org?.generalReferralRatePct ?? 10
      );
      setPayoutFrequency(org?.payoutFrequency || enterprise?.referralPreview?.payoutFrequency || 'monthly');
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, enterprise, org]);

  if (!isOpen) return null;

  const basePrice = enterprise?.referralPreview?.priceWithoutDiscount || 99;
  const discountVal = Math.min(100, Math.max(0, Number(memberDiscountPercent) || 0));
  const discountedPrice = Math.round(basePrice * (1 - discountVal / 100) * 100) / 100;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      memberDiscountPercent: discountVal,
      rosterListedRatePct: Math.min(100, Math.max(0, Number(rosterListedRatePct) || 0)),
      generalReferralRatePct: Math.min(100, Math.max(0, Number(generalReferralRatePct) || 0)),
      payoutFrequency,
    });
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        backdropFilter: 'blur(4px)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#FFFFFF',
          borderRadius: 16,
          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
          width: '100%',
          maxWidth: 520,
          padding: '24px 28px',
          animation: 'fadeUp 0.2s ease-out',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 18 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 19, fontWeight: 700, color: '#0F172A', display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="fas fa-sliders" style={{ color: '#F97316' }} />
              Edit Referral Group Offer
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748B' }}>
              Configure member discount &amp; owner revenue share rates for <strong>{org?.groupName || org?.name || 'this group'}</strong>.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ border: 'none', background: 'none', fontSize: 20, color: '#94A3B8', cursor: 'pointer', padding: 4 }}
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* 1. Member Discount Offered (%) */}
            <div>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 13, color: '#334155', marginBottom: 6 }}>
                1. Member Discount Offered (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                className="cm-search-input"
                style={{ width: '100%', height: 40, border: '1px solid #CBD5E1', borderRadius: 8, padding: '0 12px', fontSize: 14 }}
                value={memberDiscountPercent}
                onChange={(e) => setMemberDiscountPercent(e.target.value)}
                placeholder="e.g. 2, 10, or 0"
                required
              />
              <div style={{ marginTop: 6, fontSize: 12, color: '#15803D', background: '#F0FDF4', padding: '8px 12px', borderRadius: 8, border: '1px solid #BBF7D0', display: 'flex', alignItems: 'center', gap: 6 }}>
                <i className="fas fa-calculator" />
                Member Annual Price: <strong style={{ fontSize: 13 }}>${discountedPrice.toFixed(2)} / year</strong> (Base catalog: ${basePrice.toFixed(2)})
              </div>
            </div>

            {/* 2. Roster-Listed Payout Rate (%) */}
            <div>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 13, color: '#334155', marginBottom: 6 }}>
                2. Roster-Listed Payout Rate (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                className="cm-search-input"
                style={{ width: '100%', height: 40, border: '1px solid #CBD5E1', borderRadius: 8, padding: '0 12px', fontSize: 14 }}
                value={rosterListedRatePct}
                onChange={(e) => setRosterListedRatePct(e.target.value)}
                placeholder="e.g. 15 or 20"
                required
              />
              <span style={{ display: 'block', fontSize: 11, color: '#64748B', marginTop: 4 }}>
                Group owner revenue share when an officially listed roster member joins.
              </span>
            </div>

            {/* 3. General Referral Payout Rate (%) */}
            <div>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 13, color: '#334155', marginBottom: 6 }}>
                3. General Referral Payout Rate (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                className="cm-search-input"
                style={{ width: '100%', height: 40, border: '1px solid #CBD5E1', borderRadius: 8, padding: '0 12px', fontSize: 14 }}
                value={generalReferralRatePct}
                onChange={(e) => setGeneralReferralRatePct(e.target.value)}
                placeholder="e.g. 10 or 5"
                required
              />
              <span style={{ display: 'block', fontSize: 11, color: '#64748B', marginTop: 4 }}>
                Group owner revenue share when a general referral user joins via group link/code.
              </span>
            </div>

            {/* 4. Payout Frequency */}
            <div>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 13, color: '#334155', marginBottom: 6 }}>
                4. Payout Schedule
              </label>
              <select
                className="cm-filter-select"
                style={{ width: '100%', height: 40, border: '1px solid #CBD5E1', borderRadius: 8, padding: '0 12px', fontSize: 14 }}
                value={payoutFrequency}
                onChange={(e) => setPayoutFrequency(e.target.value)}
              >
                <option value="monthly">Monthly</option>
                <option value="semi_monthly">Semi-monthly</option>
                <option value="quarterly">Quarterly</option>
              </select>
            </div>
          </div>

          <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button
              type="button"
              className="action-btn"
              style={{ background: '#F1F5F9', border: '1px solid #CBD5E1', color: '#475569', height: 38, padding: '0 16px', borderRadius: 8 }}
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="action-btn btn-green"
              style={{ height: 38, padding: '0 20px', borderRadius: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}
              disabled={saving}
            >
              <i className="fas fa-floppy-disk" />
              {saving ? 'Saving…' : 'Save Offer Settings'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
